import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AiJob, ResponseDetail, ResponseTransition } from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  Field,
  Input,
  LoadingPane,
  StatusBadge,
  Tabs,
  Textarea,
  useToast,
} from '../../components/ui.js';
import { Markdown } from '../../components/Markdown.js';
import { MarkdownEditor } from './components/MarkdownEditor.js';
import { CommentsPanel } from './components/CommentsPanel.js';
import { EvidencePanel } from './components/EvidencePanel.js';
import { VersionHistory } from './components/VersionHistory.js';
import { AiAssist } from './components/AiAssist.js';
import { SnapshotImport } from './components/SnapshotImport.js';
import './response.css';

const EDITABLE = ['not_started', 'in_progress', 'drafted', 'changes_requested'];

interface ActionSpec {
  action: ResponseTransition;
  label: string;
  show: (r: ResponseDetail, role: string) => boolean;
  variant?: 'primary' | 'secondary' | 'danger';
  confirm?: string;
  withNote?: boolean;
}

const ACTIONS: ActionSpec[] = [
  {
    action: 'mark_drafted',
    label: 'Mark as drafted',
    variant: 'secondary',
    show: (r, role) =>
      r.status === 'in_progress' && ['admin', 'contributor'].includes(role) && r.currentVersion > 0,
  },
  {
    action: 'submit_for_review',
    label: 'Submit for review',
    show: (r, role) =>
      r.status === 'drafted' && r.reviewState !== 'approved' && ['admin', 'contributor'].includes(role),
  },
  {
    action: 'approve',
    label: 'Approve',
    show: (r, role) => r.status === 'under_review' && ['admin', 'reviewer'].includes(role),
  },
  {
    action: 'request_changes',
    label: 'Request changes',
    variant: 'secondary',
    withNote: true,
    show: (r, role) => r.status === 'under_review' && ['admin', 'reviewer'].includes(role),
  },
  {
    action: 'finalise',
    label: 'Finalise',
    confirm:
      'Finalising freezes this response for assessor review. It can only be reopened by an admin before assessment.',
    show: (r, role) => r.status === 'drafted' && r.reviewState === 'approved' && role === 'admin',
  },
  {
    action: 'reopen',
    label: 'Reopen',
    variant: 'secondary',
    confirm: 'Reopening returns the response to editing and clears its finalised state.',
    show: (r, role) => r.status === 'finalised' && role === 'admin',
  },
  {
    action: 'mark_assessed',
    label: 'Mark as assessed',
    confirm: 'Marking as assessed records your assessment outcome for this response.',
    show: (r, role) => r.status === 'finalised' && role === 'assessor',
  },
];

/** Structured answers (yes_no etc.) are stored as JSON content. */
function parseStructured(content: string): { answer?: string; notes?: string } {
  try {
    const parsed = JSON.parse(content);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

export function ResponsePage() {
  const { assessmentId = '', responseId = '' } = useParams();
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const response = useQuery({
    queryKey: ['response', responseId],
    queryFn: () => apiFetch<ResponseDetail>(`/responses/${responseId}`),
  });

  const r = response.data;
  const role = org.role;
  // Organisation AI switch: when off the AI tab is omitted for everyone.
  const aiAvailable = org.aiEnabled !== false && (role === 'admin' || role === 'contributor');
  const serverContent = r?.content?.content ?? '';
  const [draft, setDraft] = useState<string>('');
  const [changeSummary, setChangeSummary] = useState('');
  const [structAnswer, setStructAnswer] = useState<string>('');
  const [structNotes, setStructNotes] = useState<string>('');
  const [aiJobId, setAiJobId] = useState<string | null>(null);
  const [panel, setPanel] = useState<'comments' | 'evidence' | 'history' | 'ai'>('comments');
  const draftKey = `zest.draft.${responseId}`;

  // The AI panel can disappear underneath an open tab (the org toggles AI off
  // while this page is open) — fall back to Comments rather than a blank pane.
  useEffect(() => {
    if (panel === 'ai' && !aiAvailable) setPanel('comments');
  }, [panel, aiAvailable]);

  const responseType = r?.question.responseType ?? 'markdown';
  const structured = ['yes_no', 'multiple_choice', 'date', 'number'].includes(responseType);

  // Initialise editor state from the server (and any local autosave).
  useEffect(() => {
    if (!r) return;
    if (structured) {
      const parsed = parseStructured(serverContent);
      setStructAnswer(parsed.answer ?? '');
      setStructNotes(parsed.notes ?? '');
      return;
    }
    const saved = localStorage.getItem(draftKey);
    if (saved && saved !== serverContent) {
      setDraft(saved);
    } else {
      setDraft(serverContent);
    }
  }, [r?.id, r?.currentVersion]);

  // Local autosave for markdown drafts.
  useEffect(() => {
    if (structured || !r) return;
    const timer = setTimeout(() => {
      if (draft && draft !== serverContent) localStorage.setItem(draftKey, draft);
    }, 800);
    return () => clearTimeout(timer);
  }, [draft, serverContent, structured, draftKey, r]);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['response', responseId] });
    void queryClient.invalidateQueries({ queryKey: ['responses', assessmentId] });
    void queryClient.invalidateQueries({ queryKey: ['assessment', assessmentId] });
    void queryClient.invalidateQueries({ queryKey: ['versions', responseId] });
  }, [queryClient, responseId, assessmentId]);

  const onError = useCallback(
    (err: unknown) =>
      toast(err instanceof ApiRequestError ? err.message : 'Something went wrong', 'error'),
    [toast],
  );

  const save = useMutation({
    mutationFn: () => {
      const body = structured
        ? {
            content: JSON.stringify({ answer: structAnswer, notes: structNotes }),
            contentFormat: 'json' as const,
            changeSummary: changeSummary.trim(),
          }
        : {
            content: draft,
            contentFormat: 'markdown' as const,
            changeSummary: changeSummary.trim(),
            ...(aiJobId ? { aiJobId } : {}),
          };
      return apiFetch(`/responses/${responseId}/content`, { method: 'PUT', body });
    },
    onSuccess: () => {
      localStorage.removeItem(draftKey);
      setChangeSummary('');
      setAiJobId(null);
      toast('Saved as a new version.', 'success');
      invalidate();
    },
    onError,
  });

  const [confirmAction, setConfirmAction] = useState<ActionSpec | null>(null);
  const [noteAction, setNoteAction] = useState<ActionSpec | null>(null);
  const [note, setNote] = useState('');
  const transition = useMutation({
    mutationFn: ({ action, actionNote }: { action: ResponseTransition; actionNote?: string }) =>
      apiFetch(`/responses/${responseId}/transition`, {
        method: 'POST',
        body: { action, note: actionNote },
      }),
    onSuccess: (_, vars) => {
      toast(`Done: ${vars.action.replaceAll('_', ' ')}.`, 'success');
      setConfirmAction(null);
      setNoteAction(null);
      setNote('');
      invalidate();
    },
    onError,
  });

  const checklist = useMutation({
    mutationFn: (complete: boolean) =>
      apiFetch(`/responses/${responseId}/checklist`, { method: 'POST', body: { complete } }),
    onSuccess: invalidate,
    onError,
  });

  const acceptAi = useCallback(
    (job: AiJob) => {
      setDraft(job.outputContent ?? '');
      setAiJobId(job.id);
      setChangeSummary('Accepted AI draft');
      setPanel('comments');
      toast('AI draft loaded into the editor — review it, then save.', 'success');
    },
    [toast],
  );

  const runAction = (spec: ActionSpec) => {
    if (spec.withNote) setNoteAction(spec);
    else if (spec.confirm) setConfirmAction(spec);
    else transition.mutate({ action: spec.action });
  };

  const visibleActions = useMemo(
    () => (r ? ACTIONS.filter((a) => a.show(r, role)) : []),
    [r, role],
  );

  if (response.isLoading) return <LoadingPane label="Loading response" />;
  if (!r) return null;

  const canEdit =
    ['admin', 'contributor'].includes(role) && EDITABLE.includes(r.status);
  const unsaved = structured
    ? JSON.stringify({ answer: structAnswer, notes: structNotes }) !==
      (serverContent || JSON.stringify({ answer: '', notes: '' }))
    : draft !== serverContent;

  const options: string[] = Array.isArray(r.question.options)
    ? (r.question.options as string[])
    : [];

  return (
    <div>
      <p>
        <Link to={`/assessments/${assessmentId}`}>← Back to assessment</Link>
      </p>

      <div className="question-block">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 480px' }}>
            <p style={{ color: 'var(--color-text-faint)', marginBottom: 4 }}>
              Clause {r.clause.clauseNumber} — {r.clause.title}
            </p>
            <h1>{r.question.text}</h1>
            {r.question.helpText && <p className="question-help">{r.question.helpText}</p>}
            {(r.clause.description || r.clause.guidanceNotes) && (
              <details className="clause-context">
                <summary>Clause context and guidance</summary>
                <div className="body">
                  {r.clause.description && <p>{r.clause.description}</p>}
                  {r.clause.guidanceNotes && <p>{r.clause.guidanceNotes}</p>}
                  {r.clause.evidenceRequirements && (
                    <p>
                      <strong>Evidence expectations:</strong> {r.clause.evidenceRequirements}
                    </p>
                  )}
                </div>
              </details>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <StatusBadge status={r.status} reviewState={r.reviewState} />
            <div style={{ marginTop: 12 }}>
              <label
                style={{
                  display: 'inline-flex',
                  gap: 8,
                  alignItems: 'center',
                  fontSize: 'var(--text-sm)',
                }}
              >
                <input
                  type="checkbox"
                  checked={r.checklistComplete}
                  disabled={!canEdit || checklist.isPending}
                  onChange={(e) => checklist.mutate(e.target.checked)}
                />
                Checklist complete
              </label>
            </div>
          </div>
        </div>
      </div>

      {!canEdit && (
        <div className="status-banner">
          <StatusBadge status={r.status} reviewState={r.reviewState} />
          <span>
            {r.status === 'under_review'
              ? 'This response is with the reviewer — content is frozen until the review completes.'
              : ['finalised', 'assessed', 'locked'].includes(r.status)
                ? 'This response is read-only. Any amendment needs a reopen (before assessment) or a new assessment cycle.'
                : ['admin', 'contributor'].includes(role)
                  ? 'This response is not currently editable.'
                  : 'Your role has read access to this response.'}
          </span>
        </div>
      )}

      <div className="response-layout">
        <div>
          {canEdit ? (
            <Card>
              {responseType === 'markdown' || responseType === 'table' || responseType === 'file_upload' ? (
                <>
                  {responseType === 'file_upload' && (
                    <p style={{ color: 'var(--color-text-muted)' }}>
                      This question is primarily answered with evidence files (see the Evidence
                      tab). Use the editor for supporting commentary.
                    </p>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <SnapshotImport
                      responseId={responseId}
                      unsaved={unsaved}
                      onImported={() => localStorage.removeItem(draftKey)}
                    />
                  </div>
                  <MarkdownEditor value={draft} onChange={setDraft} />
                </>
              ) : responseType === 'yes_no' ? (
                <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                  <legend className="field-label">Answer</legend>
                  {['yes', 'no'].map((v) => (
                    <label key={v} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input
                        type="radio"
                        name="yes-no"
                        value={v}
                        checked={structAnswer === v}
                        onChange={() => setStructAnswer(v)}
                      />
                      {v === 'yes' ? 'Yes' : 'No'}
                    </label>
                  ))}
                  <Field label="Notes" hint="Optional supporting detail">
                    {(props) => (
                      <Textarea
                        {...props}
                        value={structNotes}
                        onChange={(e) => setStructNotes(e.target.value)}
                      />
                    )}
                  </Field>
                </fieldset>
              ) : responseType === 'multiple_choice' ? (
                <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                  <legend className="field-label">Answer</legend>
                  {options.map((option) => (
                    <label key={option} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input
                        type="radio"
                        name="multiple-choice"
                        value={option}
                        checked={structAnswer === option}
                        onChange={() => setStructAnswer(option)}
                      />
                      {option}
                    </label>
                  ))}
                  <Field label="Notes" hint="Optional supporting detail">
                    {(props) => (
                      <Textarea
                        {...props}
                        value={structNotes}
                        onChange={(e) => setStructNotes(e.target.value)}
                      />
                    )}
                  </Field>
                </fieldset>
              ) : (
                <>
                  <Field label="Answer" required>
                    {(props) => (
                      <Input
                        {...props}
                        type={responseType === 'date' ? 'date' : 'number'}
                        value={structAnswer}
                        onChange={(e) => setStructAnswer(e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Notes" hint="Optional supporting detail">
                    {(props) => (
                      <Textarea
                        {...props}
                        value={structNotes}
                        onChange={(e) => setStructNotes(e.target.value)}
                      />
                    )}
                  </Field>
                </>
              )}

              <div className="save-bar">
                <Field label="Change summary" hint="Recorded in the version history">
                  {(props) => (
                    <Input
                      {...props}
                      value={changeSummary}
                      onChange={(e) => setChangeSummary(e.target.value)}
                      placeholder="What changed?"
                    />
                  )}
                </Field>
                <Button
                  loading={save.isPending}
                  disabled={!unsaved && r.currentVersion > 0}
                  onClick={() => save.mutate()}
                >
                  Save version
                  {unsaved && <span className="unsaved-dot" aria-label="Unsaved changes" />}
                </Button>
              </div>
              {aiJobId && (
                <p style={{ marginTop: 8 }}>
                  <Badge tone="purple">AI draft pending acceptance</Badge>{' '}
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    Saving will record this version as AI-generated.
                  </span>
                </p>
              )}
            </Card>
          ) : (
            <Card title="Response">
              {serverContent ? (
                structured ? (
                  <dl>
                    <dt style={{ fontWeight: 650 }}>Answer</dt>
                    <dd style={{ marginLeft: 0, marginBottom: 12 }}>
                      {parseStructured(serverContent).answer || '—'}
                    </dd>
                    {parseStructured(serverContent).notes && (
                      <>
                        <dt style={{ fontWeight: 650 }}>Notes</dt>
                        <dd style={{ marginLeft: 0 }}>{parseStructured(serverContent).notes}</dd>
                      </>
                    )}
                  </dl>
                ) : (
                  <Markdown markdown={serverContent} />
                )
              ) : (
                <p style={{ color: 'var(--color-text-muted)' }}>No response recorded yet.</p>
              )}
              {r.content?.aiGenerated && (
                <p style={{ marginTop: 8 }}>
                  <Badge tone="purple">Contains accepted AI content</Badge>
                </p>
              )}
            </Card>
          )}

          {visibleActions.length > 0 && (
            <div className="action-bar">
              {visibleActions.map((spec) => (
                <Button
                  key={spec.action}
                  variant={spec.variant ?? 'primary'}
                  loading={transition.isPending}
                  onClick={() => runAction(spec)}
                >
                  {spec.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        <Card className="panel-card">
          <Tabs
            tabs={[
              { key: 'comments' as const, label: `Comments${r.openReviewerComments + r.openAssessorComments > 0 ? ` (${r.openReviewerComments + r.openAssessorComments})` : ''}` },
              { key: 'evidence' as const, label: `Evidence${r.evidenceCount > 0 ? ` (${r.evidenceCount})` : ''}` },
              { key: 'history' as const, label: 'History' },
              ...(aiAvailable ? [{ key: 'ai' as const, label: 'AI assist' }] : []),
            ]}
            active={panel}
            onChange={setPanel}
          />
          {panel === 'comments' && <CommentsPanel response={r} />}
          {panel === 'evidence' && <EvidencePanel response={r} />}
          {panel === 'history' && <VersionHistory response={r} />}
          {panel === 'ai' && aiAvailable && <AiAssist response={r} onAccept={acceptAi} />}
        </Card>
      </div>

      <Dialog
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        title={confirmAction?.label ?? ''}
      >
        <p>{confirmAction?.confirm}</p>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setConfirmAction(null)}>
            Cancel
          </Button>
          <Button
            loading={transition.isPending}
            onClick={() => confirmAction && transition.mutate({ action: confirmAction.action })}
          >
            {confirmAction?.label}
          </Button>
        </div>
      </Dialog>

      <Dialog open={!!noteAction} onClose={() => setNoteAction(null)} title={noteAction?.label ?? ''}>
        <Field label="Note for the author" hint="Optional — explain what needs to change">
          {(props) => <Textarea {...props} value={note} onChange={(e) => setNote(e.target.value)} />}
        </Field>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setNoteAction(null)}>
            Cancel
          </Button>
          <Button
            loading={transition.isPending}
            onClick={() =>
              noteAction && transition.mutate({ action: noteAction.action, actionNote: note.trim() || undefined })
            }
          >
            {noteAction?.label}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
